import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';
import './planning-a-lesson.css';

const KnowYourStudents = () => {
  const title = 'Know Your Students';
  const image = '/Images/online-teacher.webp';
  const intro = "The most powerful teaching tool isn't a textbook or an app — it's what you know about the person across from you. This guide covers what to learn in the first lesson, how to record it, and how to turn those notes into effortless small talk that builds rapport and personalizes every session.";

  const seoContent = [];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={[]}
      seoTitle="Know Your Students: First Lesson Notes, Needs Analysis & Small Talk Strategies"
      seoDescription="Learn what to ask in the first English lesson, how to take structured student notes, and how to use those details for personalized small talk that builds rapport and makes every lesson relevant."
      datePublished="2026-10-18"
      dateModified="2026-10-18"
    >
      <div className="teach-content">
        {/* Overview Box */}
        <div className="overview-box">
          <h2>Why Student Knowledge Matters</h2>
          <p>
            Students don't care how much you know until they know how much you care. Remembering that 
            Maria's daughter just started university, or that Ahmed prefers tea over coffee, or that 
            Yuki's team just launched a new product — these details transform a transactional lesson 
            into a relationship. And relationships drive retention, motivation, and progress.
          </p>
          <p>
            <strong>The first lesson is your intake interview.</strong> Everything you learn there becomes 
            fuel for the next 50 lessons.
          </p>
        </div>

        {/* What to Capture */}
        <section className="content-section">
          <h2>What to Capture in the First Lesson</h2>
          <p>Treat the intro lesson as a structured conversation, not a test. Cover these areas naturally:</p>

          <div className="mode-comparison">
            <div className="mode-card learning">
              <h3>Personal & Background</h3>
              <ul>
                <li>Name, preferred name, pronunciation</li>
                <li>Age / life stage (student, professional, retiree, parent)</li>
                <li>Where they live / time zone / cultural context</li>
                <li>Where they're from originally</li>
                <li>Family situation (partner, kids, pets, elderly parents)</li>
              </ul>
            </div>
            <div className="mode-card conversation">
              <h3>Work & Study</h3>
              <ul>
                <li>Current role / industry / company type</li>
                <li>Typical workday (meetings, emails, presentations, shifts)</li>
                <li>English use at work (internal, client-facing, technical, social)</li>
                <li>Education background / field of study</li>
                <li>Career goals / promotions / job changes on horizon</li>
              </ul>
            </div>
          </div>

          <div className="mode-comparison" style={{marginTop: '1rem'}}>
            <div className="mode-card learning">
              <h3>English Journey</h3>
              <ul>
                <li>Years studying English / previous teachers / methods</li>
                <li>Self-assessed strengths & weaknesses</li>
                <li>Most frustrating aspect (grammar, listening, confidence, vocab)</li>
                <li>Favorite / least favorite English activities</li>
                <li>Target level or exam goal (if any)</li>
              </ul>
            </div>
            <div className="mode-card conversation">
              <h3>Interests & Life</h3>
              <ul>
                <li>Hobbies, sports, creative outlets</li>
                <li>Reading / watching / listening preferences (genres, topics)</li>
                <li>Travel experience / dream destinations</li>
                <li>Weekend routines / how they recharge</li>
                <li>Current obsessions (shows, games, projects, causes)</li>
              </ul>
            </div>
          </div>

          <div className="tip">
            <strong>Pro tip:</strong> You don't need a form. Have a natural conversation. Take notes 
            <em>during</em> or immediately after. Students open up more when it feels like chat, not interrogation.
          </div>
        </section>

        {/* How to Record */}
        <section className="content-section">
          <h2>How to Record & Organize Notes</h2>
          <p>Consistency beats comprehensiveness. A simple system you actually use is better than a perfect one you abandon.</p>

          <div className="stage">
            <div className="stage-content">
              <h3><span>1. One Page Per Student</span></h3>
              <p>A single A4 / digital page. Sections: Personal, Work/Study, English, Interests, Notes. 
              Update after every lesson. Physical index cards, Notion page, Google Doc, spreadsheet — pick one and stick with it.</p>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>2. The "Small Talk Bank"</span></h3>
              <p>Extract 5–8 conversation starters from their notes and keep them at the top of the page:</p>
              <ul>
                <li><em>"How's the new project at [company]?"</em></li>
                <li><em>"Did [child's name] settle into university?"</em></li>
                <li><em>"Still playing tennis on weekends?"</em></li>
                <li><em>"Any trips planned to [dream destination]?"</em></li>
                <li><em>"How was that [show/game] you mentioned?"</em></li>
              </ul>
              <p>Before each lesson, glance at the bank. Pick one. Zero prep, instant connection.</p>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>3. Update in Real Time</span></h3>
              <p>During small talk, listen for new details. Jot them down immediately:</p>
              <ul>
                <li>New job / promotion / restructure</li>
                <li>Health updates (theirs or family)</li>
                <li>Upcoming travel / moves / life events</li>
                <li>New hobbies / shows / interests</li>
                <li>Frustrations or wins with English</li>
              </ul>
              <p>The bank grows. The relationship deepens. Lessons feel personal, not programmed.</p>
            </div>
          </div>
        </section>

        {/* Using Notes in Lessons */}
        <section className="content-section">
          <h2>Turning Notes into Lesson Fuel</h2>

          <div className="stage">
            <div className="stage-content">
              <h3><span>Small Talk Openers (3–5 min every lesson)</span></h3>
              <p>Reference a specific detail, not a generic "how are you":</p>
              <ul>
                <li><strong>Generic:</strong> "How was your weekend?"</li>
                <li><strong>Personalized:</strong> "You mentioned your daughter's graduation — how did it go?"</li>
              </ul>
              <p>This signals: <em>I remember. I care. You matter.</em> Students relax, speak more freely, and bring their real language needs.</p>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>Topic Selection</span></h3>
              <p>Choose lesson topics from their world:</p>
              <ul>
                <li>Healthcare worker → medical communication, patient explanations</li>
                <li>Software dev → code reviews, stand-ups, technical documentation</li>
                <li>Parent → school meetings, extracurriculars, bedtime routines</li>
                <li>Traveler → airports, hotels, cultural misunderstandings</li>
              </ul>
              <p>When the topic is their life, motivation is automatic.</p>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>Example & Practice Contexts</span></h3>
              <p>Frame grammar/vocab in their reality:</p>
              <ul>
                <li><em>Present perfect:</em> "Tell me about projects you've led this year" (not "Have you ever been to Paris?")</li>
                <li><em>Conditionals:</em> "If your manager asked you to work Saturday, what would you say?"</li>
                <li><em>Phrasal verbs:</em> Pull from their emails/meetings</li>
              </ul>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>Error Correction Priorities</span></h3>
              <p>Note fossilized errors that affect <em>their</em> communication:</p>
              <ul>
                <li>Ahmed (IT) always says "discuss about" → target "discuss"</li>
                <li>Maria (HR) confuses "actually" / "currently" → clarify</li>
                <li>Yuki (finance) drops articles in presentations → drill "the quarterly report"</li>
              </ul>
            </div>
          </div>
        </section>

        {/* First Lesson Flow */}
        <section className="content-section">
          <h2>The First Lesson: A Practical Flow</h2>
          <p>45–60 minutes. Conversation-first, notes-second.</p>

          <div className="mode-comparison">
            <div className="mode-card learning">
              <h3>1. Warm Welcome (5 min)</h3>
              <ul>
                <li>Smile, use their name, thank them for coming</li>
                <li>Explain the flow: "We'll chat, I'll learn about you, we'll do a little English"</li>
                <li>Set tone: relaxed, no wrong answers</li>
              </ul>
            </div>
            <div className="mode-card conversation">
              <h3>2. Guided Conversation (20 min)</h3>
              <ul>
                <li>Work through the four areas naturally</li>
                <li>Follow interesting threads</li>
                <li>Take brief notes (don't hide them — "Mind if I jot this down?")</li>
              </ul>
            </div>
          </div>

          <div className="mode-comparison" style={{marginTop: '1rem'}}>
            <div className="mode-card learning">
              <h3>3. Quick Level Check (10 min)</h3>
              <ul>
                <li>Picture description or short reading</li>
                <li>2–3 discussion questions on a familiar topic</li>
                <li>Listen for: fluency, vocab range, grammar control, pronunciation</li>
                <li>No scoring — just mental CEFR placement</li>
              </ul>
            </div>
            <div className="mode-card conversation">
              <h3>4. Goals & Preferences (10 min)</h3>
              <ul>
                <li>"What would make you feel successful in 3 months?"</li>
                <li>"How do you like to be corrected?"</li>
                <li>"Homework: yes/no? How much?"</li>
                <li>"Topics to avoid / topics to prioritize?"</li>
              </ul>
            </div>
          </div>

          <div className="mode-comparison" style={{marginTop: '1rem'}}>
            <div className="mode-card learning">
              <h3>5. Wrap Up (5 min)</h3>
              <ul>
                <li>Summarize what you heard: "So you're a [role], you want to [goal], you enjoy [interest]"</li>
                <li>Confirm: "Did I get that right?"</li>
                <li>Preview next lesson: "Next time we'll look at [topic from their world]"</li>
              </ul>
            </div>
            <div className="mode-card conversation">
              <h3>6. Immediate Note-Dump (Post-lesson)</h3>
              <ul>
                <li>Spend 5 min filling your template</li>
                <li>Build the Small Talk Bank (5–8 starters)</li>
                <li>Flag 2–3 priority errors</li>
                <li>Note preferred correction style</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Ongoing Maintenance */}
        <section className="content-section">
          <h2>Keeping Notes Alive</h2>

          <div className="stage">
            <div className="stage-content">
              <h3><span>After Every Lesson (1 min)</span></h3>
              <p>Add: new personal detail, language win, persistent error, topic request, mood/energy level.</p>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>Monthly Review (5 min)</span></h3>
              <p>Scan notes. What's changed? New job? New goal? Stagnant error? Adjust lesson planning accordingly. 
              Ask student: "Anything new I should know?" — they'll appreciate being asked.</p>
            </div>
          </div>

          <div className="stage">
            <div className="stage-content">
              <h3><span>Quarterly Deep Clean (15 min)</span></h3>
              <p>Archive outdated details. Refresh the Small Talk Bank. Rewrite goals if they've shifted. 
              Celebrate progress: "When we started, you hesitated on past tense — now you're telling stories fluidly."</p>
            </div>
          </div>
        </section>

        {/* Checklist */}
        <section className="content-section checklist">
          <h2>First-Lesson Note-Taking Checklist</h2>
          <ul>
            <li>Preferred name & pronunciation confirmed</li>
            <li>Location / time zone / cultural context noted</li>
            <li>Work/study role, industry, daily English use captured</li>
            <li>English history: years, methods, self-assessment, frustrations</li>
            <li>Goals: specific, measurable, time-bound (or "none — just maintain")</li>
            <li>Correction preference: immediate / delayed / selective / none</li>
            <li>Homework appetite confirmed</li>
            <li>5+ personal interests / hobbies / life details recorded</li>
            <li>Small Talk Bank created (5–8 personalized openers)</li>
            <li>2–3 priority fossilized errors flagged</li>
            <li>Next lesson topic previewed and noted</li>
          </ul>
        </section>
      </div>
    </TeachEnglishPageLayout>
  );
};

export default KnowYourStudents;